/**
 * 本地昵称 ↔ 账号昵称的对齐。
 *
 * 判定规则在 `useAuth.ts` 的 `decideNickname`；这里负责两件事：
 *   - 账号每变一次就对齐一次（`watchAccountNickname`，由 `getAuthSession` 挂上）；
 *   - 把「以本地为准」的名字真的存到账号上（`pushNickname`），有了结果再清掉「没存上」标记。
 * 另一个入口是大厅昵称框失焦 / 点「换一个」（`Lobby` 的 NicknameRow，判据见 `shouldPushOnCommit`）。
 */
import type { PublicAccount } from '@poker/shared'

import type { AuthChange, AuthSession } from '../auth/session.js'
import { ProfileError, saveNickname } from './profileApi.js'
import { useNicknameStore } from './useAuth.js'

export type SaveNickname = (nickname: string) => Promise<PublicAccount>

/**
 * ★ 存名字排成一队、一次一个：连着改两次（Bob → Alice），两个 PATCH 并发的话服务端可能后到先存，
 *   最后停在 Bob，本地却是 Alice。排队以后服务端最后存下的一定是最后一次想存的名字；
 *   排队期间又被更新的名字顶掉的请求直接跳过，不白发。
 */
let tail: Promise<void> = Promise.resolve()
let seq = 0
let latest: { readonly key: string; readonly seq: number; readonly done: Promise<void> } | null = null

/**
 * 把本地名字存到账号上。尽力而为：失败时名字保持「没存上」，下次对齐或下次失焦再补；
 * 服务端判为不合法（400）的名字不再重试，交给下次对齐以账号为准。
 * 返回的 promise 会把失败抛出来。
 */
export function pushNickname(accountId: string, nickname: string, save: SaveNickname = saveNickname): Promise<void> {
  const key = `${accountId}\n${nickname}`
  // 同一个名字已经在队里（大厅两处同时对齐、失焦连点）：跟着那一次走
  if (latest?.key === key) return latest.done
  const mine = ++seq
  const done = tail.then(async () => {
    if (latest !== null && latest.seq !== mine) return // 被后面的名字顶掉了
    try {
      const acc = await save(nickname)
      // 用服务端回来的账号 id：请求飞行期间换了号，这次存到的是谁就记谁
      useNicknameStore.getState().markSynced(acc.id, nickname)
    } catch (e) {
      if (e instanceof ProfileError && e.kind === 'invalid') useNicknameStore.getState().markSynced(accountId, nickname)
      throw e
    }
  })
  const settle = (): void => {
    if (latest?.seq === mine) latest = null
  }
  tail = done.then(settle, settle)
  latest = { key, seq: mine, done }
  return done
}

/** 拿到账号后对齐名字；需要时顺手补存。永不抛。 */
export function syncNicknameWithAccount(
  account: PublicAccount,
  change?: AuthChange | null,
  save: SaveNickname = saveNickname,
): Promise<void> {
  const verdict = useNicknameStore.getState().reconcile(account, change)
  if (verdict !== 'push') return Promise.resolve()
  return pushNickname(account.id, useNicknameStore.getState().nickname, save).catch(() => undefined)
}

/** 账号每变一次就对齐一次。出错、登出的通知不带新资料，跳过。 */
export function watchAccountNickname(auth: AuthSession, save: SaveNickname = saveNickname): () => void {
  return auth.onChange((s) => {
    if (s.account === null || s.change === 'error' || s.change === 'logout') return
    void syncNicknameWithAccount(s.account, s.change, save)
  })
}

/**
 * 昵称框失焦 / 点「换一个」时要不要存。
 * ★ 只存用户改过、还没存上的名字。以前的判据是「和账号上的不一样就存」，
 *   于是别的设备改过名、这台还没对齐时，点一下输入框再点走就把旧名字存回去了。
 */
export function shouldPushOnCommit(state: { readonly nickname: string; readonly dirty: boolean }): boolean {
  return state.dirty && state.nickname !== ''
}

/** 测试用 */
export function __resetNicknameSync(): void {
  tail = Promise.resolve()
  seq = 0
  latest = null
}
